/**
 * @file OrderPaymentTermsField.tsx
 * @description Условия оплаты заказа: предоплата или при получении
 * (phase19.24 шаг 5.1, §2 «Оплата»).
 *
 * Что можно, решает реестр исходов (`RECEIPT_METHOD_META[method].paymentTerms`,
 * правило F31): у доставки ТК «при получении» запрещено, и опция показывается
 * отключённой С ПРИЧИНОЙ, а не исчезает — иначе менеджер не понимает, куда
 * делся привычный вариант и почему сервер отвечает `carrier_requires_prepay`.
 *
 * Юрлицо платит по счёту: способ подписан отдельно, а сам счёт выставляется
 * после подтверждения условий (прежний аннулируется).
 */

import {
  PAYMENT_TERMS_LABEL,
  RECEIPT_METHOD_META,
  isPaymentTermsAllowed,
} from "../../lib/receipt-method";
import type { OrderPaymentTerms, OrderReceiptMethod } from "../../lib/api-orders";
import { NoticeStrip } from "../ui/NoticeStrip";
import { SectionLabel } from "../ui/SectionLabel";
import { cn } from "@/lib/utils";

/** Порядок опций: сначала «при получении» — так их читает менеджер по телефону. */
const TERMS_ORDER: OrderPaymentTerms[] = ["POSTPAY", "PREPAY"];

interface Props {
  method: OrderReceiptMethod;
  value: OrderPaymentTerms;
  onChange: (next: OrderPaymentTerms) => void;
  /** Клиент — юрлицо: оплата идёт безналичным счётом. */
  legalEntity?: boolean;
  disabled?: boolean;
  idPrefix?: string;
}

export function OrderPaymentTermsField({
  method,
  value,
  onChange,
  legalEntity,
  disabled,
  idPrefix = "terms",
}: Props) {
  const meta = RECEIPT_METHOD_META[method];

  return (
    <div className="flex flex-col gap-3">
      <SectionLabel as="h2">Оплата</SectionLabel>

      <div role="radiogroup" aria-label="Условия оплаты" className="flex flex-col gap-2 lg:flex-row">
        {TERMS_ORDER.map((terms) => {
          const allowed = isPaymentTermsAllowed(method, terms);
          return (
            <label
              key={terms}
              className={cn(
                "flex min-h-14 flex-1 cursor-pointer items-center gap-3 rounded-xl px-4 py-3 lg:min-h-12",
                value === terms && allowed ? "bg-primary/10" : "bg-surface-low",
                (!allowed || disabled) && "cursor-not-allowed opacity-60",
              )}
            >
              <input
                type="radio"
                name={`${idPrefix}-payment`}
                className="size-4 accent-[var(--primary)]"
                checked={value === terms}
                disabled={disabled || !allowed}
                onChange={() => onChange(terms)}
              />
              <span className="min-w-0">
                <span className="block text-base font-medium text-on-surface">
                  {legalEntity && terms === "PREPAY"
                    ? "Безналичный счёт"
                    : PAYMENT_TERMS_LABEL[terms]}
                </span>
                {!allowed && meta.paymentRestriction ? (
                  <span className="block text-sm text-on-surface-variant">
                    {meta.paymentRestriction}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>

      {method === "PICKUP" ? (
        <p className="text-sm text-on-surface-variant">
          Самовывоз: по умолчанию оплата при выдаче. Предоплата — если клиент просит.
        </p>
      ) : null}

      {legalEntity ? (
        <NoticeStrip>
          Клиент — юрлицо: счёт выставляется после подтверждения условий,
          действующий счёт будет аннулирован.
        </NoticeStrip>
      ) : null}
    </div>
  );
}
